import { useCallback, useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import {
  CreateVenueRequestDto,
  VenueDto,
  VenueSourceCandidateDto,
  VenueType,
} from '@ivy-monorepo/shared'
import { adminService } from 'src/services/admin.service'
import { BaseModalProps } from 'src/types/modal'
import { loadGoogleMaps } from 'src/utils/googleMaps'

const DEFAULT_CENTER = { lat: 41.8827, lng: -87.6233 }
const SEARCH_HALF_SIZE_DEGREES = 0.008

type VenueFormState = {
  name: string
  type: VenueType
  latitude: string
  longitude: string
  address: string
  postalCode: string
  telephone: string
  overtureId: string
  osmId: string
  northeastLatitude: string
  northeastLongitude: string
  southwestLatitude: string
  southwestLongitude: string
}

const emptyForm = (): VenueFormState => ({
  name: '',
  type: VenueType.Restaurant,
  latitude: '',
  longitude: '',
  address: '',
  postalCode: '',
  telephone: '',
  overtureId: '',
  osmId: '',
  northeastLatitude: '',
  northeastLongitude: '',
  southwestLatitude: '',
  southwestLongitude: '',
})

function bboxFromCenter(lat: number, lng: number, halfSize = SEARCH_HALF_SIZE_DEGREES) {
  return {
    southwestLatitude: (lat - halfSize).toFixed(6),
    southwestLongitude: (lng - halfSize).toFixed(6),
    northeastLatitude: (lat + halfSize).toFixed(6),
    northeastLongitude: (lng + halfSize).toFixed(6),
  }
}

function candidateToForm(candidate: VenueSourceCandidateDto): VenueFormState {
  return {
    name: candidate.name ?? '',
    type: candidate.type ?? VenueType.Other,
    latitude: String(candidate.latitude ?? ''),
    longitude: String(candidate.longitude ?? ''),
    address: candidate.address ?? candidate.name ?? '',
    postalCode: candidate.postalCode ?? '',
    telephone: candidate.telephone ?? '',
    overtureId: candidate.overtureId ?? '',
    osmId: candidate.osmId ?? '',
    northeastLatitude: String(candidate.northeastLatitude ?? ''),
    northeastLongitude: String(candidate.northeastLongitude ?? ''),
    southwestLatitude: String(candidate.southwestLatitude ?? ''),
    southwestLongitude: String(candidate.southwestLongitude ?? ''),
  }
}

type AddVenueModalProps = BaseModalProps & {
  onCreated?: () => void
  /** Prefills the form on open, e.g. when creating a venue from an approved suggestion. */
  initialValues?: Partial<VenueFormState>
  /** Overrides how the form is submitted — defaults to adminService.createVenue. */
  onSubmit?: (payload: CreateVenueRequestDto) => Promise<VenueDto>
}

const AddVenueModal = ({ isOpen, onClose, onCreated, initialValues, onSubmit }: AddVenueModalProps) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<any>(null)
  const rectangleRef = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const boundsListenerRef = useRef<any>(null)
  const syncingFromMapRef = useRef(false)

  const initialBbox = bboxFromCenter(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng)
  const [southwestLatitude, setSouthwestLatitude] = useState(initialBbox.southwestLatitude)
  const [southwestLongitude, setSouthwestLongitude] = useState(initialBbox.southwestLongitude)
  const [northeastLatitude, setNortheastLatitude] = useState(initialBbox.northeastLatitude)
  const [northeastLongitude, setNortheastLongitude] = useState(initialBbox.northeastLongitude)
  const [searchTerms, setSearchTerms] = useState('')
  const [candidates, setCandidates] = useState<VenueSourceCandidateDto[]>([])
  const [sourceErrors, setSourceErrors] = useState<string[]>([])
  const [selectedProviderKey, setSelectedProviderKey] = useState<string | null>(null)
  const [form, setForm] = useState<VenueFormState>(emptyForm)
  const [isSearching, setIsSearching] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mapReady, setMapReady] = useState(false)

  const applyBbox = useCallback((bbox: ReturnType<typeof bboxFromCenter>) => {
    setSouthwestLatitude(bbox.southwestLatitude)
    setSouthwestLongitude(bbox.southwestLongitude)
    setNortheastLatitude(bbox.northeastLatitude)
    setNortheastLongitude(bbox.northeastLongitude)
  }, [])

  const resetState = useCallback(() => {
    const bbox = bboxFromCenter(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng)
    applyBbox(bbox)
    setSearchTerms('')
    setCandidates([])
    setSourceErrors([])
    setSelectedProviderKey(null)
    setForm(emptyForm())
    setError(null)
    setIsSearching(false)
    setIsSaving(false)
  }, [applyBbox])

  useEffect(() => {
    if (!isOpen) {
      resetState()
    }
  }, [isOpen, resetState])

  useEffect(() => {
    if (!isOpen || !initialValues) {
      return
    }
    setForm({ ...emptyForm(), ...initialValues })
    const lat = Number(initialValues.latitude)
    const lng = Number(initialValues.longitude)
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      applyBbox(bboxFromCenter(lat, lng))
    }
  }, [isOpen, initialValues, applyBbox])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    let cancelled = false

    loadGoogleMaps()
      .then(() => {
        if (cancelled || !mapContainerRef.current || mapRef.current) {
          setMapReady(true)
          return
        }

        const map = new window.google.maps.Map(mapContainerRef.current, {
          center: DEFAULT_CENTER,
          zoom: 14,
          mapTypeId: 'roadmap',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        })
        mapRef.current = map

        map.addListener('click', (event: any) => {
          const lat = event.latLng?.lat()
          const lng = event.latLng?.lng()
          if (typeof lat === 'number' && typeof lng === 'number') {
            applyBbox(bboxFromCenter(lat, lng))
          }
        })

        setMapReady(true)
      })
      .catch(() => {
        setError('Map failed to load. You can still enter coordinates manually.')
        setMapReady(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, applyBbox])

  useEffect(() => {
    if (!isOpen || !mapRef.current || !window.google?.maps) {
      return
    }

    const bounds = {
      south: Number(southwestLatitude),
      west: Number(southwestLongitude),
      north: Number(northeastLatitude),
      east: Number(northeastLongitude),
    }
    if (Object.values(bounds).some(Number.isNaN)) {
      return
    }

    if (!rectangleRef.current) {
      rectangleRef.current = new window.google.maps.Rectangle({
        map: mapRef.current,
        editable: true,
        draggable: true,
        strokeColor: '#5A8A7C',
        strokeOpacity: 0.95,
        strokeWeight: 2,
        fillColor: '#5A8A7C',
        fillOpacity: 0.2,
        bounds,
      })

      boundsListenerRef.current = rectangleRef.current.addListener('bounds_changed', () => {
        const next = rectangleRef.current?.getBounds()
        if (!next) {
          return
        }
        syncingFromMapRef.current = true
        const sw = next.getSouthWest()
        const ne = next.getNorthEast()
        setSouthwestLatitude(sw.lat().toFixed(6))
        setSouthwestLongitude(sw.lng().toFixed(6))
        setNortheastLatitude(ne.lat().toFixed(6))
        setNortheastLongitude(ne.lng().toFixed(6))
      })
      mapRef.current.fitBounds(bounds, 40)
      return
    }

    if (syncingFromMapRef.current) {
      syncingFromMapRef.current = false
      return
    }

    rectangleRef.current.setBounds(bounds)
    mapRef.current.fitBounds(bounds, 40)
  }, [isOpen, southwestLatitude, southwestLongitude, northeastLatitude, northeastLongitude])

  useEffect(() => {
    if (!isOpen || !mapRef.current || !window.google?.maps) {
      return
    }

    const lat = Number(form.latitude)
    const lng = Number(form.longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      markerRef.current?.setMap(null)
      return
    }

    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        map: mapRef.current,
        title: 'Venue location',
      })
    }

    markerRef.current.setPosition({ lat, lng })
    markerRef.current.setMap(mapRef.current)
  }, [isOpen, form.latitude, form.longitude])

  useEffect(() => {
    if (isOpen) {
      return
    }

    boundsListenerRef.current?.remove()
    boundsListenerRef.current = null
    rectangleRef.current?.setMap(null)
    rectangleRef.current = null
    markerRef.current?.setMap(null)
    markerRef.current = null
    mapRef.current = null
    setMapReady(false)
  }, [isOpen])

  const updateField = <K extends keyof VenueFormState>(key: K, value: VenueFormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const useSquareCenter = () => {
    const lat = (Number(southwestLatitude) + Number(northeastLatitude)) / 2
    const lng = (Number(southwestLongitude) + Number(northeastLongitude)) / 2
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return
    }
    updateField('latitude', lat.toFixed(6))
    updateField('longitude', lng.toFixed(6))
  }

  const searchCandidates = async () => {
    setIsSearching(true)
    setError(null)
    setSourceErrors([])

    try {
      const response = await adminService.getVenueSourceCandidates({
        southwestLatitude,
        southwestLongitude,
        northeastLatitude,
        northeastLongitude,
        searchTerms: searchTerms.trim() || undefined,
      })
      setCandidates(response.candidates)
      setSourceErrors(response.errors ?? [])
      if (response.candidates.length === 0 && (response.errors?.length ?? 0) === 0) {
        setError('No places found in this area')
      }
    } catch {
      setCandidates([])
      setError('Failed to search places in this area')
    } finally {
      setIsSearching(false)
    }
  }

  const applyCandidate = (candidate: VenueSourceCandidateDto) => {
    if (candidate.alreadyExists) {
      return
    }
    setSelectedProviderKey(candidate.providerKey)
    setForm(candidateToForm(candidate))
    setError(null)
  }

  const canSubmit =
    form.name.trim().length > 0 &&
    form.address.trim().length > 0 &&
    Number.isFinite(Number(form.latitude)) &&
    Number.isFinite(Number(form.longitude)) &&
    !isSaving

  const submit = async () => {
    if (!canSubmit) {
      return
    }

    const payload: CreateVenueRequestDto = {
      name: form.name.trim(),
      type: form.type,
      latitude: Number(form.latitude),
      longitude: Number(form.longitude),
      address: form.address.trim(),
      postalCode: form.postalCode.trim() || null,
      telephone: form.telephone.trim() || null,
      overtureId: form.overtureId.trim() || null,
      osmId: form.osmId.trim() || null,
    }

    if (
      form.northeastLatitude &&
      form.northeastLongitude &&
      form.southwestLatitude &&
      form.southwestLongitude
    ) {
      payload.northeastLatitude = Number(form.northeastLatitude)
      payload.northeastLongitude = Number(form.northeastLongitude)
      payload.southwestLatitude = Number(form.southwestLatitude)
      payload.southwestLongitude = Number(form.southwestLongitude)
    }

    setIsSaving(true)
    setError(null)
    try {
      await (onSubmit ? onSubmit(payload) : adminService.createVenue(payload))
      onCreated?.()
      onClose()
    } catch (e: any) {
      const message =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        'Failed to create venue'
      setError(typeof message === 'string' ? message : 'Failed to create venue')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      className="modal-popup add-venue-popup"
      overlayClassName="modal-overlay"
      onRequestClose={onClose}
    >
      <div className="venue-add-popup-content modal-content-wrapper">
        <div className="add-venue-header">
          <h2>Add Venue</h2>
          <p className="add-venue-subtitle">
            Search Overture/OSM in a map square to prefill, or enter details manually. Nothing is saved until you create.
          </p>
        </div>

        {(isSearching || isSaving) && (
          <div className="loader-wrapper">
            <div className="loader" />
          </div>
        )}

        <div className="add-venue-layout">
          <div className="add-venue-search-pane">
            <div
              ref={mapContainerRef}
              className="add-venue-map"
              aria-label="Search area map"
            />
            {!mapReady && (
              <div className="add-venue-map-fallback">
                Map unavailable — enter coordinates in the form.
              </div>
            )}

            <div className="add-venue-search-controls">
              <div className="form-group">
                <label>Name filter (optional)</label>
                <input
                  value={searchTerms}
                  onChange={e => setSearchTerms(e.target.value)}
                  placeholder="Filter places by name"
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void searchCandidates()
                    }
                  }}
                />
              </div>
              <div className="add-venue-search-actions">
                <button
                  type="button"
                  className="styled-button sm"
                  onClick={() => void searchCandidates()}
                  disabled={isSearching || isSaving}
                >
                  <span>{isSearching ? 'Searching…' : 'Search area'}</span>
                </button>
                <button
                  type="button"
                  className="styled-button sm"
                  onClick={useSquareCenter}
                  disabled={isSaving}
                >
                  <span>Use square center</span>
                </button>
              </div>
              <p className="add-venue-hint">
                Click the map to place a square, then drag or resize its corners.
              </p>
            </div>

            {sourceErrors.length > 0 && (
              <div className="add-venue-source-errors">
                {sourceErrors.map(message => (
                  <div key={message}>{message}</div>
                ))}
              </div>
            )}

            <div className="external-venues-list">
              {candidates.length === 0 && !isSearching && (
                <div className="add-venue-empty">No candidates yet — search an area first.</div>
              )}
              {candidates.map(candidate => {
                const isSelected = selectedProviderKey === candidate.providerKey
                return (
                  <div
                    key={candidate.providerKey}
                    className={`external-venue-wrapper${isSelected ? ' selected' : ''}${candidate.alreadyExists ? ' already-exists' : ''}`}
                  >
                    <div className="venue-info">
                      <div className="venue-details">
                        <div className="venue-name">{candidate.name}</div>
                        <div className="venue-address">{candidate.address || 'No address'}</div>
                        <div className="venue-meta">
                          {candidate.type}
                          {' · '}
                          {candidate.dataSources.join('+')}
                          {candidate.alreadyExists ? ' · Already in Ivy' : ''}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="styled-button sm"
                      onClick={() => applyCandidate(candidate)}
                      disabled={candidate.alreadyExists || isSaving}
                      title={candidate.alreadyExists ? 'Already exists in Ivy' : 'Prefill form'}
                    >
                      <span>{candidate.alreadyExists ? 'Exists' : isSelected ? 'Selected' : 'Use'}</span>
                    </button>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="add-venue-form-pane">
            <div className="form-group">
              <label>Name</label>
              <input
                value={form.name}
                onChange={e => updateField('name', e.target.value)}
                placeholder="Venue name"
              />
            </div>

            <div className="form-group">
              <label>Type</label>
              <select
                value={form.type}
                onChange={e => updateField('type', e.target.value as VenueType)}
              >
                {Object.values(VenueType).map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Address</label>
              <input
                value={form.address}
                onChange={e => updateField('address', e.target.value)}
                placeholder="Street address"
              />
            </div>

            <div className="location-inputs">
              <div className="form-group">
                <label>Latitude</label>
                <input
                  value={form.latitude}
                  onChange={e => updateField('latitude', e.target.value)}
                  placeholder="41.8827"
                />
              </div>
              <div className="form-group">
                <label>Longitude</label>
                <input
                  value={form.longitude}
                  onChange={e => updateField('longitude', e.target.value)}
                  placeholder="-87.6233"
                />
              </div>
            </div>

            <div className="location-inputs">
              <div className="form-group">
                <label>Postal code</label>
                <input
                  value={form.postalCode}
                  onChange={e => updateField('postalCode', e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div className="form-group">
                <label>Telephone</label>
                <input
                  value={form.telephone}
                  onChange={e => updateField('telephone', e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            {(form.overtureId || form.osmId) && (
              <div className="add-venue-source-ids">
                {form.overtureId ? <span>Overture: {form.overtureId}</span> : null}
                {form.osmId ? <span>OSM: {form.osmId}</span> : null}
              </div>
            )}

            {error && <div className="add-venue-error">{error}</div>}

            <div className="add-venue-form-actions">
              <button
                type="button"
                className="styled-button sm"
                onClick={onClose}
                disabled={isSaving}
              >
                <span>Cancel</span>
              </button>
              <button
                type="button"
                className="styled-button sm"
                onClick={() => void submit()}
                disabled={!canSubmit}
              >
                <span>{isSaving ? 'Creating…' : 'Create venue'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default AddVenueModal
